import qs from 'qs'
import r2rServicesClient from 'common/utils/r2rServicesClient'
import moment from 'moment'
import actions from './actions'
import GeocoderResult from 'tickets/utils/geocoderResult.js'
import R2RDateTime from 'common/utils/R2RDateTime/R2RDateTime'

import { contains } from 'common/utils/arrayUtils'
import { MODE_IDS } from 'common/utils/constants'
import {
  logGaNonInteractionEvent,
  logGaEvent,
  logBackendEvent,
  logBackendSearch,
} from 'common/utils/analytics'
import {
  ticketableSchedulesEndpoint,
  flightSchedulesEndpoint,
  flightDetailsEndpoint,
  flightPollingEndpoint,
} from 'common/utils/paths'
import { dateFormat, dateTimeFormat } from 'common/utils/time'
import {
  FERRY_TYPES,
  PASSENGER_TYPES,
  MIN_BUFFER_MINUTES,
  DEFAULT_SEARCH_HOUR,
  DEFAULT_SEARCH_HOUR_STRING,
} from 'tickets/utils/constants'
import { errorCodes } from 'tickets/utils/errors'
import {
  buildUrl,
  getCleanPaxFromUrl,
  getDebugFlagsFromUrl,
} from 'tickets/utils/urlUtils'
import {
  setPreviousOriginCookieFromLongName,
  setPreviousDestinationCookieFromLongName,
  setPreviousTicketOriginCookieFromLongName,
  setPreviousTicketDestinationCookieFromLongName,
} from 'tickets/utils/cookies'

import { deserializeResponse } from 'tickets/redux/modules/searchResponses/deserialize'
import modesInfoOperations from '../modesInfo/operations'
import searchConfigOperations from '../searchConfig/operations'
import locationOperations from '../location/operations'
import searchParamsOperations from '../searchParams/operations'
import filterOperators from '../filters/operations'
import { getFilters, getActiveFerryType } from 'tickets/redux/modules/filters/selectors'
import { getModesInfo } from 'tickets/redux/modules/modesInfo/selectors'
import {
  getSearchIsValid,
  getSearchIsReturn,
  getIsDropOff,
  getHandoverModeId,
} from 'tickets/redux/modules/searchParams/selectors'
import {
  getLastOutboundTicketTime,
  getLastInboundTicketTime,
  getOutboundAndInboundBookableTogether,
  getSearchResponses,
  getSelectedOutboundArrivalDateTime,
  getDepartureDateTimeFromItinerary,
} from 'tickets/redux/modules/searchResponses/selectors'
import {
  getHasSelectedOutboundItinerary,
  getHasSelectedInboundItinerary,
} from 'tickets/redux/modules/selections/selectors'
import {
  getHasAvailableModes,
  getAvailableModeIds,
  getSurfaceData,
  getFlightData,
} from 'tickets/redux/modules/searchConfig/selectors'
import { getResolvedDetailsLinks } from 'tickets/redux/modules/resolvedDetailsLinks/selectors'
import { getCurrencyCode, getLanguageCode } from 'tickets/redux/modules/user/selectors'
import {
  beginTicketSearchExpiryHandler,
} from 'tickets/redux/modules/user/searchTimeout'
import {
  getSearchOrigin,
  getSearchDestination,
  getSearchOutboundDateTime,
  getSearchInboundDateTime,
  getSelectedOutboundItineraryId,
  getSelectedInboundItineraryId,
  getCurrentModeId,
  getCurrentSearchURL,
} from 'tickets/redux/modules/router/selectors'
import history from 'common/utils/history'
import selectionsOperations from 'tickets/redux/modules/selections/operations'
import searchResponsesOperations from 'tickets/redux/modules/searchResponses/operations'

import userOperations from 'tickets/redux/modules/user/operations'

let lastUpdateSearchRequestTimestamp
const performSearch = () => (dispatch, getState) => {
  const state = getState()
  if (!getSearchIsValid(getState())) {
    return
  }
  dispatch(loadingResponses(state))

  const originCanonical = getSearchOrigin(state)
  const destinationCanonical = getSearchDestination(state)

  sendSearch(originCanonical, destinationCanonical)(dispatch, getState)
}

const sendSearch = (originCanonical, destinationCanonical) => (dispatch, getState) => {
  const requestTimestamp = new Date().getTime()
  lastUpdateSearchRequestTimestamp = requestTimestamp
    // this request is redundant (except on page load)
  // it's also happening when change orig/dest
  // but it's being done again to ensure we don't do a ticketable schedules
  // request with an outdated config
  const configPromise = dispatch(
    searchConfigOperations.fetchAvailableModes(originCanonical,
      destinationCanonical, requestTimestamp)
  )

  const currencyPromise = dispatch(userOperations.fetchCurrencies())

  configPromise
    .then(() => {
      if (getHasAvailableModes(getState())) {
        dispatch(sendSearchRequests(requestTimestamp, currencyPromise))
      } else {
        const oName = getSearchOrigin(getState())
        const dName = getSearchDestination(getState())
        logGaNonInteractionEvent(
          'Tickets',
          'NoValidResults',
          `${oName} to ${dName}`
      )
      }
    })
    .catch((error) => {
      logGaNonInteractionEvent(
        'Tickets',
        'API:Config',
        error
      )
    })
}

const sendSearchRequests = (requestTimestamp, currencyPromise) => (dispatch, getState) => {
  dispatch(actions.logPerformSearch())
  const state = getState()

  getAvailableModeIds(state).forEach((modeId) => {
    dispatch(modesInfoOperations.modeLoading(modeId))

    const ticketSearchPromise = dispatch(sendTicketSearchRequest(modeId))
    Promise.all([ticketSearchPromise, currencyPromise])
      .then((responses) => {
        const ticketResponse = responses[0]
        if (lastUpdateSearchRequestTimestamp === requestTimestamp) {
          const deserializedResponse = deserializeResponse(ticketResponse.data)

          let fallbackTimeFilter = null
          let resetTimeFilter = null

          // Find what time the time filter should be set to upon finishing loading results.
          // This is for situations where there's no itineraries after the default search time
          // and so we need to wind back the time filter to match the received results
          if (hasValidItineraries(deserializedResponse, true)) {
            let earliestDepartureTime = getEarliestDepartureDateTime(deserializedResponse, true)
            let latestDepartureTimeOnEarliestDate = getLatestDepartureDateTimeOnEarliestDate(deserializedResponse, true)

            if (earliestDepartureTime != null && latestDepartureTimeOnEarliestDate != null) {
              let defaultFilterTime = new R2RDateTime(earliestDepartureTime.date, DEFAULT_SEARCH_HOUR_STRING)

              // If we're automatically setting departure time...
              if (state.filters[modeId].isTimeManuallySet == false) {
                // ... and there's no itinerary that is after the default time on the earliest found date,
                // set the value of the time filter to match the earliest itinerary
                if (latestDepartureTimeOnEarliestDate.isBefore(defaultFilterTime)) {
                  fallbackTimeFilter = earliestDepartureTime.toMomentUTC()
                }
                // ...else ensure the value of the time filter is reset to the default search time (deals with
                // previous searches that go into the above case and alters the search time)
                else {
                  resetTimeFilter = defaultFilterTime.toMomentUTC()
                }
            }
            }
          }

          const apiMessages = getApiMessages(deserializedResponse)
          const apiErrors = getApiErrors(deserializedResponse)

          dispatch(modesInfoOperations.setTabApiMessages(modeId, apiMessages, apiErrors))
          dispatch(actions.addResponse(deserializedResponse, modeId, true, null))
          beginTicketSearchExpiryHandler(dispatch, getState)

          const totalItineraries = getTotalNumOfItineraries(deserializedResponse)

          logGaNonInteractionEvent('Tickets', 'API:TicketSearch', modeId,
                                    totalItineraries.toString())

          if (totalItineraries == 0) {
            logGaNonInteractionEvent(
              'Tickets',
              `API:NoResults:${modeId}`,
              `${getSearchOrigin(getState())}/${getSearchDestination(getState())}`
            )

            if (getHandoverModeId(state) == modeId) {
              // the user entered with a mode=modeId, this is a failed handover
              logGaNonInteractionEvent(
                'FailedHandover', `NoResults:${modeId}`,
                `${getSearchOrigin(getState())}/${getSearchDestination(getState())}`
              )
            }
          }

          const pollingInfo = ticketResponse.data.header.pollingInfo
          if (pollingInfo) {
            dispatch(pollSearchRequests(modeId, pollingInfo))
          } else {
            // Dispatch time filter alterations for itineraries with only early results if required
            if (fallbackTimeFilter != null) {
              dispatch(actions.changingTime(modeId, true, fallbackTimeFilter))
            }
            else if (resetTimeFilter != null) {
              dispatch(actions.changingTime(modeId, true, resetTimeFilter))
            }

            dispatch(modesInfoOperations.modeLoaded(modeId))
          }
        }
      })
      .catch((error) => {
        /* eslint-disable no-console */
        console.log('error', error)
        /* eslint-enable no-console */
        dispatch(searchFailed(modeId))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch', `Error:${error}`
        )

        if (getHandoverModeId(state) == modeId) {
          // the user entered with a mode=modeId, this is a failed handover
          logGaNonInteractionEvent(
            'FailedHandover', `ResponseError:${modeId}`,
            `${getSearchOrigin(getState())}/${getSearchDestination(getState())}`
          )
        }
      })
      .then(() => {
        // handover modeId only required for initial page load
        if (getHandoverModeId(state) == modeId) {
          dispatch(searchParamsOperations.updateHandoverMode(null))
        }
      })
  })
}

const getTotalNumOfItineraries = (ticketSearchResponse) => {
  const nOutbound = ticketSearchResponse.outboundItineraries.length
  const nInbound = ticketSearchResponse.inboundItineraries
    ? ticketSearchResponse.inboundItineraries.length : 0

  return (nOutbound + nInbound)
}

const getApiMessages = (ticketSearchResponse) => {
  // Default - Messages passed from backend
  const apiMessages = ticketSearchResponse.header.messageDetails || []

  // Add Specific Error Code Messages
  if (responseHasErrorCode(ticketSearchResponse.header, errorCodes.PROVIDER_CONNECTION_ERROR)
      && getTotalNumOfItineraries(ticketSearchResponse) > 0) {
    // Warn User That Only Partial Results Have Been Loaded Due To Provider(s) Being Down
    apiMessages.push(errorCodes.PROVIDER_CONNECTION_ERROR)
  }
  return apiMessages
}

const getApiErrors = (ticketSearchResponse) => {
  // Only return error when no results were returned
  if (getTotalNumOfItineraries(ticketSearchResponse) == 0) {
    // Return errors if any were supplied
    return ticketSearchResponse.header.errorDetails
  }
  return []
}

// Note: the only mode that polls is flights
let lastPollSearchRequestTimestamp
const pollSearchRequests = (modeId, pollingInfo) => (dispatch, getState) => {
  dispatch(modesInfoOperations.modePolling(modeId))

  const requestTimestamp = new Date().getTime()
  lastPollSearchRequestTimestamp = requestTimestamp

  const state = getState()
  sendApiRequest(state, flightPollingEndpoint, { data: pollingInfo })
    .then((response) => {
      // Discard if we're expecting a later response
      if (lastPollSearchRequestTimestamp !== requestTimestamp) {
        return
      }

      // Polling may be stopped by triggering a new search
      const newState = getState()
      const isStillPolling = getModesInfo(newState)[modeId].isPolling
      if (!isStillPolling) {
        return
      }

      // Throw away the new response if it doesn't contain the user's selected tickets
      // Note we don't stop polling because the selected ticket may return in a later poll
      const deserializedResponse = deserializeResponse(response.data)
      if (!responseContainsSelectedItineraries(newState, deserializedResponse)) {
        logGaNonInteractionEvent('Tickets', 'API:TicketSearchPolling', 'SkippedResponse')
      } else {
        const nOutbound = response.data.outboundItineraries.length
        const nInbound = response.data.inboundItineraries
          ? response.data.inboundItineraries.length : 0
        const totalLength = nOutbound + nInbound
        logGaNonInteractionEvent('Tickets', 'API:TicketSearchPolling', modeId, totalLength)

        const detailsLinks = getResolvedDetailsLinks(newState)
        const apiMessages = getApiMessages(deserializedResponse)
        const apiErrors = getApiErrors(deserializedResponse)

        dispatch(modesInfoOperations.setTabApiMessages(modeId, apiMessages, apiErrors))
        dispatch(actions.replaceEntireResponse(deserializedResponse, modeId))
        dispatch(actions.reapplyDetailsLinks(modeId, detailsLinks))
      }

      const nextPollingInfo = response.data.header.pollingInfo
      if (nextPollingInfo) {
        dispatch(pollSearchRequests(modeId, nextPollingInfo))
      } else {
        logGaNonInteractionEvent('Tickets', 'API:TicketSearchPolling', 'FinishedPolling')
        dispatch(modesInfoOperations.modeLoaded(modeId))
      }
    })
    .catch((error) => {
      /* eslint-disable no-console */
      console.log('error', error)
      /* eslint-enable no-console */
      dispatch(modesInfoOperations.modeLoaded(modeId))
      logGaNonInteractionEvent('Tickets', 'API:TicketSearchPolling', `Error:${error}`)
    })
}

// This should only be called for the inbound journey
const forceTimeChange = () => (dispatch, getState) => {
  const state = getState()
  const modeId = getCurrentModeId(state)
  const isOutbound = false
  const outboundArrivalDateTime = getSelectedOutboundArrivalDateTime(state).toMomentUTC()

  logGaEvent('Tickets', 'ForceTimeChange', outboundArrivalDateTime.format(dateTimeFormat))

  // Add one minute to the final outbound time so we don't get duplicate outbound journeys back
  const oDateTime = moment(getLastOutboundTicketTime(state))
    .add(1, 'minutes').format(dateTimeFormat)
  // Add one hour as a minimum
  const iDateTime = outboundArrivalDateTime
    .add(MIN_BUFFER_MINUTES, 'minutes').format(dateTimeFormat)

  const paramOverrides = {
    oDateTime,
    iDateTime,
    removeBeforeSearchTime: true,
  }

  dispatch(actions.changingTime(modeId, isOutbound, iDateTime))

  dispatch(sendTicketSearchRequest(modeId, paramOverrides))
    .then((response) => {
      if (hasValidItineraries(response.data, isOutbound)) {
        const deserializedResponse = deserializeResponse(response.data)
        dispatch(actions.replaceResponse(deserializedResponse, modeId, isOutbound))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:ForceTimeChange', modeId, totalItineraryCount(response.data)
        )
        dispatch(modesInfoOperations.modeLoaded(modeId))
      } else {
        dispatch(modesInfoOperations.noResultsForTime(modeId, isOutbound))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:ForceTimeChange', `No More Results ${modeId}`
        )
      }
    })
    .catch((error) => {
      /* eslint-disable no-console */
      console.log('error', error)
      /* eslint-enable no-console */
      dispatch(searchFailed(modeId))

      logGaNonInteractionEvent(
        'Tickets', 'API:TicketSearch:ForceTimeChange', `Error:${error}`
      )
    })
}

const fetchMore = (modeId, isOutbound) => (dispatch, getState) => {
  const state = getState()

  const oDateTime = moment(getLastOutboundTicketTime(state))
      .add(1, 'minutes').format(dateTimeFormat)
  const iDateTime = !isOutbound
    ? moment(getLastInboundTicketTime(state))
        .add(1, 'minutes').format(dateTimeFormat)
    : null

  const paramOverrides = {
    oDateTime,
    iDateTime,
    removeBeforeSearchTime: true,
  }

  dispatch(sendTicketSearchRequest(modeId, paramOverrides))
    .then((response) => {
      if (hasValidItineraries(response.data, isOutbound)) {
        const deserializedResponse = deserializeResponse(response.data)
        dispatch(modesInfoOperations.increasePage(modeId))
        dispatch(actions.addResponse(deserializedResponse, modeId, false, isOutbound))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:More', modeId, totalItineraryCount(response.data)
        )
      } else {
        dispatch(modesInfoOperations.noMoreResults(isOutbound, modeId))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:More', `No More Results:${modeId}`
        )
      }
    })
    .catch((error) => {
      /* eslint-disable no-console */
      console.log('error', error)
      /* eslint-enable no-console */
      dispatch(searchFailed(modeId))

      logGaNonInteractionEvent(
        'Tickets', 'API:TicketSearch:More', `Error:${error}`
      )
    })
}

const timeChange = (time, isOutbound) => (dispatch, getState) => {
  const state = getState()
  const modeId = getCurrentModeId(state)
  const queryObject = qs.parse(state.router.location.search.slice(1))

  logGaEvent('Tickets', 'TimeChanged', time)
  logBackendEvent('TimeChanged', {
    time,
  })

  let oDateTime
  let iDateTime
  // This assumes that the datetimes in queryObject are at midnight (which is why we can add stuff)
  if (isOutbound) {
    oDateTime = moment(queryObject.oDateTime).add(time, 'hours').format(dateTimeFormat)

    if (getSearchIsReturn(state)) {
      iDateTime = moment(getLastInboundTicketTime(state)).add(1, 'minutes').format(dateTimeFormat)
    }
  } else {
    oDateTime = moment(getLastOutboundTicketTime(state)).add(1, 'minutes').format(dateTimeFormat)
    iDateTime = moment(queryObject.iDateTime).add(time, 'hours').format(dateTimeFormat)
  }

  const paramOverrides = {
    oDateTime,
    iDateTime,
    removeBeforeSearchTime: false,
    isTimeManuallySet: true
  }

  dispatch(actions.changingTime(modeId, isOutbound, isOutbound ? oDateTime : iDateTime))

  dispatch(sendTicketSearchRequest(modeId, paramOverrides))
    .then((response) => {
      if (hasValidItineraries(response.data, isOutbound)) {
        const deserializedResponse = deserializeResponse(response.data)
        dispatch(actions.replaceResponse(deserializedResponse, modeId, isOutbound))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:ChangeTime', modeId, totalItineraryCount(response.data)
        )
        dispatch(modesInfoOperations.modeLoaded(modeId))
      } else {
        dispatch(modesInfoOperations.noResultsForTime(modeId, isOutbound))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:ChangeTime', `No Results for time :${modeId}`
        )
      }
    })
    .catch((error) => {
      /* eslint-disable no-console */
      console.log('error', error)
      /* eslint-enable no-console */
      dispatch(searchFailed(modeId))

      logGaNonInteractionEvent(
        'Tickets', 'API:TicketSearch:ChangeTime', `Error:${error}`
      )
    })
}

const ferryTypeChange = (currentFerryType) => (dispatch, getState) => {
  const state = getState()
  const modeId = getCurrentModeId(state)

  const ferryType = currentFerryType === FERRY_TYPES.car ? FERRY_TYPES.foot : FERRY_TYPES.car
  logGaEvent('Tickets', 'FerryTypeChanged', ferryType)
  dispatch(actions.changingFerryType(modeId, ferryType))

  dispatch(sendTicketSearchRequest(modeId))
    .then((response) => {
      const deserializedResponse = deserializeResponse(response.data)
      const apiMessages = getApiMessages(deserializedResponse)
      const apiErrors = getApiErrors(deserializedResponse)

      dispatch(modesInfoOperations.setTabApiMessages(modeId, apiMessages, apiErrors))
      dispatch(actions.replaceEntireResponse(deserializedResponse, modeId))

      const nItins = totalItineraryCount(response.data)
      if (nItins > 0) {
        dispatch(modesInfoOperations.modeLoaded(modeId))
        logGaNonInteractionEvent('Tickets', 'API:TicketSearch:FerryTypeChange', ferryType, nItins)
      } else {
        dispatch(modesInfoOperations.noResultsForFerryType(modeId))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:FerryTypeChange', `No Results for ferryType: ${ferryType}`
        )
      }
    })
    .catch((error) => {
      dispatch(searchFailed(modeId))
      logGaNonInteractionEvent(
        'Tickets', 'API:TicketSearch:FerryTypeChange', `Error:${error}`
      )
    })
}

// Used when updating user information for transfers
const updateTransfer = (transferInformation) => (dispatch, getState) => {
  const state = getState()
  const modeId = getCurrentModeId(state)
  const isDropOff = getIsDropOff(state)
  const oDateTime = transferInformation.departureDateTime.format(dateTimeFormat)
  dispatch(filterOperators.updateTransfer(
    modeId,
    isDropOff,
    transferInformation.longName,
    transferInformation.googlePlaceId,
    transferInformation.departureDateTime,
    transferInformation.returnDateTime,
  ))

  const paramOverrides = {
    oDateTime,
    removeBeforeSearchTime: false,
  }

  if (getSearchIsReturn(state)) {
    paramOverrides.iDateTime = transferInformation.returnDateTime.format(dateTimeFormat)
  }


  dispatch(sendTicketSearchRequest(modeId, paramOverrides))
    .then((response) => {
      if (hasValidItineraries(response.data, true)) {
        const deserializedResponse = deserializeResponse(response.data)
        dispatch(actions.replaceResponse(deserializedResponse, modeId, true))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:UpdateTransfer', modeId, totalItineraryCount(response.data)
        )
        dispatch(modesInfoOperations.modeLoaded(modeId))
      } else {
        dispatch(modesInfoOperations.noResultsForTime(modeId, true))
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:UpdateTransfer', `No Results for time :${modeId}`
        )
      }
    })
    .catch((error) => {
      /* eslint-disable no-console */
      console.log('error', error)
      /* eslint-enable no-console */
      dispatch(searchFailed(modeId))

      logGaNonInteractionEvent(
        'Tickets', 'API:TicketSearch:ChangeTime', `Error:${error}`
      )
    })
}

let lastDetailsRequestTimestamp
const fetchDetails = (detailsLink, modeId) => (dispatch, getState) => {
  const state = getState()
  if (getResolvedDetailsLinks(state).hasOwnProperty(detailsLink.data)) {
    return
  }

  const sr = getSearchResponses(state)[modeId]
  const oItinId = detailsLink.outboundItinerary != null
    ? sr.outboundItineraries[detailsLink.outboundItinerary].itineraryId
    : null
  const iItinId = detailsLink.inboundItinerary != null
    ? sr.inboundItineraries[detailsLink.inboundItinerary].itineraryId
    : null

  const requestTimestamp = new Date().getTime()
  lastDetailsRequestTimestamp = requestTimestamp

  sendApiRequest(state, flightDetailsEndpoint, { data: detailsLink.data })
    .then((response) => {
      if (response.data.outboundItineraries.length === 0) {
        // This is a fail safe for incomplete data from skyscanner
        // We're going to watch and see if this is a frequent problem
        logGaNonInteractionEvent(
          'Tickets', 'API:TicketSearch:Flights', 'Error: Flights details fetch fail'
        )
        dispatch(updateDetailsFailed(detailsLink))
      } else if (lastDetailsRequestTimestamp == requestTimestamp) {
        logGaNonInteractionEvent('Tickets', 'API:TicketSearch:Flights', 'DetailsSuccess')
        dispatch(
          updateDetails(
            deserializeResponse(response.data),
            modeId,
            detailsLink,
            oItinId,
            iItinId
          )
        )
      }
    })
    .catch((error) => {
      /* eslint-disable no-console */
      console.log('error', error)
      /* eslint-enable no-console */
      // TODO: error state
    })
}

const logReturnCheckoutCount = () => (dispatch, getState) => {
  const state = getState()
  if (getSearchIsReturn(state)) {
    const count = getOutboundAndInboundBookableTogether(state) ? 1 : 2
    logGaNonInteractionEvent('Tickets', 'ReturnCheckoutCount', count)
  }
}

const searchFailed = actions.searchFailed
const loadingResponses = actions.loadingResponses
const updateDetails = actions.updateDetails
const updateDetailsFailed = actions.updateDetailsFailed

/* ==== HELPERS ==== */

const hasValidItineraries = (response, isOutbound) => {
  if (isOutbound) {
    return response.outboundItineraries && response.outboundItineraries.length > 0
  }
  return response.inboundItineraries && response.inboundItineraries.length > 0
}

const totalItineraryCount = (response) => (
  response.outboundItineraries.length +
    response.inboundItineraries ? response.inboundItineraries.length : 0
)

const fetchGeocoderResult = (longName) => {
  return GeocoderResult.getGeocoderResult(longName)
    .catch((error) => {
      logGaNonInteractionEvent(
        'Tickets',
        'API:Geocode',
        error
      )
    })
}

const refreshSearch = () => (dispatch) => {
  dispatch(selectionsOperations.clearResults())
  dispatch(searchResponsesOperations.performSearch())
}

const updateUrl = (searchParams, pax) => (dispatch, getState) => {
  const state = getState()
  const languageCode = getLanguageCode(state)
  const debugFlags = getDebugFlagsFromUrl()

  // Clear oid and iid from the url when user hits Update
  dispatch(selectionsOperations.clearResults())

  dispatch(locationOperations.changeOriginLongName(searchParams.originLng))
  dispatch(locationOperations.changeDestinationLongName(searchParams.destinationLng))
  dispatch(locationOperations.changeOriginShortName(searchParams.originShrt))
  dispatch(locationOperations.changeDestinationShortName(searchParams.destinationShrt))
  dispatch(searchParamsOperations.updateOriginKind(searchParams.originKind))
  dispatch(searchParamsOperations.updateDestinationKind(searchParams.destinationKind))

  fetchGeocoderResult(searchParams.originLng, languageCode)
    .then(geocoderResult => {
      setPreviousOriginCookieFromLongName(geocoderResult)
      setPreviousTicketOriginCookieFromLongName(geocoderResult)
      dispatch(searchParamsOperations.updateOriginLatLng(geocoderResult?.latlng))
    })

  fetchGeocoderResult(searchParams.destinationLng, languageCode)
    .then(geocoderResult => {
      setPreviousDestinationCookieFromLongName(geocoderResult)
      setPreviousTicketDestinationCookieFromLongName(geocoderResult)
      dispatch(searchParamsOperations.updateDestinationLatLng(geocoderResult?.latlng))
    })

  const newURL = buildUrl(
    searchParams.origin,
    searchParams.destination,
    searchParams.oDateTime,
    searchParams.iDateTime,
    pax,
    debugFlags,
    languageCode,
  )

  const currentSearchURL = getCurrentSearchURL(state)
  if (currentSearchURL == newURL) {
    // User hit the update search button without changing anything
    // we still want to force refresh search results
    dispatch(searchResponsesOperations.performSearch())
  }
  history.push(newURL)

  logBackendSearch({
    type: 'Ticket',
    screen: 'TicketsPage',
    provider: 'Rome2Rio',
    origin: getSearchOrigin(state),
    destination: getSearchDestination(state),
    departure_date: getSearchOutboundDateTime(state),
    return_date: getSearchInboundDateTime(state),
  })
}

const responseContainsSelectedItineraries = (state, response) => {
  const hasSelectedOutboundItinerary = getHasSelectedOutboundItinerary(state)
  const selectedOutboundItineraryId = getSelectedOutboundItineraryId(state)
  const responseHasOutboundItinerary = contains(response.outboundItineraries,
    i => i.itineraryId === selectedOutboundItineraryId)

  const hasSelectedInboundItinerary = getHasSelectedInboundItinerary(state)
  const selectedInboundItineraryId = getSelectedInboundItineraryId(state)
  const responseHasInboundItinerary = contains(response.inboundItineraries,
    i => i.itineraryId === selectedInboundItineraryId)

  if (hasSelectedOutboundItinerary && !responseHasOutboundItinerary) {
    return false
  }

  if (hasSelectedInboundItinerary && !responseHasInboundItinerary) {
    return false
  }

  return true
}

const getEarliestDepartureDateTime = (deserializedResponse, isOutbound) => {
  if (!hasValidItineraries(deserializedResponse, isOutbound)) return null

  let earliestDepartureTime = getDepartureDateTimeFromItinerary(0, isOutbound, deserializedResponse)
  let itineraries = isOutbound ? deserializedResponse.outboundItineraries : deserializedResponse.inboundItineraries
  for (let i = 1; i < itineraries.length; i++) {
    let departureTime = getDepartureDateTimeFromItinerary(i, isOutbound, deserializedResponse)
    if (departureTime.isBefore(earliestDepartureTime)) earliestDepartureTime = departureTime
  }

  return earliestDepartureTime
}

// Gets the latest dateTime on the earliest departure date in the provided itineraries
const getLatestDepartureDateTimeOnEarliestDate = (deserializedResponse, isOutbound) => {
  if (!hasValidItineraries(deserializedResponse, isOutbound)) return null

  let earliestDepartureTime = getEarliestDepartureDateTime(deserializedResponse, isOutbound)
  if (earliestDepartureTime == null) return null

  let itineraries = isOutbound ? deserializedResponse.outboundItineraries : deserializedResponse.inboundItineraries
  let earliestDate = earliestDepartureTime.date
  let latestDepartureTime = earliestDepartureTime
  for (let i = 1; i < itineraries.length; i++) {
    let departureTime = getDepartureDateTimeFromItinerary(i, isOutbound, deserializedResponse)
    if (departureTime.isAfter(latestDepartureTime) && earliestDate == departureTime.Date) {
      latestDepartureTime = departureTime
    }
  }

  return latestDepartureTime
}

const getPaxParamsForTicketableSchedulesRequest = () => {
  const paxTypes = []
  const paxAges = []
  const paxCards = []

  getCleanPaxFromUrl().forEach((passenger) => {
    if (passenger.type == PASSENGER_TYPES.youth) {
      paxTypes.push('paxchild')
      paxAges.push(passenger.age)
    } else if (passenger.type === PASSENGER_TYPES.senior) {
      // todo: change backend so just have to pass through 'paxsenior'
      paxTypes.push('paxAdult')
      const seniorAge = passenger.age ? passenger.age : '65'
      paxAges.push(seniorAge)
    } else {
      paxTypes.push('paxadult')
      paxAges.push(passenger.age)
    }

    paxCards.push(passenger.travelCard)
  })

  return {
    paxTypes: paxTypes.join('|'),
    paxAges: paxAges.join('|'),
    paxCards: paxCards.join('|'),
  }
}

// Sends a ticketableschedules or flightschedules request with params from the state
const sendTicketSearchRequest = (modeId, paramOverrides) => (dispatch, getState) => {
  const state = getState()
  const queryObject = qs.parse(state.router.location.search.slice(1))
  const filters = getFilters(state)[modeId]

  // Update filters if paramOverride provides a new value
  if (paramOverrides && paramOverrides.isTimeManuallySet) {
    filters.isTimeManuallySet = true
  }

  const isTimeManuallySet = filters.isTimeManuallySet
  let oDateTime = null
  if (isTimeManuallySet) {
    // Set search time to the user defined value
    oDateTime = moment(queryObject.oDateTime, dateFormat)
    .add(filters.outboundDepartureTime, 'hours').format(dateTimeFormat)
  } else {
    // Set search time to default value
    oDateTime = moment(queryObject.oDateTime, dateFormat)
    .add(DEFAULT_SEARCH_HOUR, 'hours').format(dateTimeFormat)
  }

  const iDateTime = queryObject.iDateTime
    ? moment(queryObject.iDateTime, dateTimeFormat)
      .add(filters.inboundDepartureTime, 'hours').format(dateTimeFormat)
    : null
  const oGooglePlaceId = filters.originGooglePlaceId
  const dGooglePlaceId = filters.destinationGooglePlaceId

  // Note that fields with null-values will not be added to the query string.
  // Fields such as removeBeforeSearchTime are given a default null value here to remind us
  // which fields may be set/overridden
  const params = {
    oName: getSearchOrigin(state),
    dName: getSearchDestination(state),
    oDateTime,
    iDateTime,
    oGooglePlaceId,
    dGooglePlaceId,
    mode: modeId,
    removeBeforeSearchTime: null,
    isTimeManuallySet,
    ferryType: getActiveFerryType(state),
    currencyCode: getCurrencyCode(state),
    languageCode: getLanguageCode(state),
    searchData: modeId === MODE_IDS.plane ? getFlightData(state) : getSurfaceData(state),
    ...getPaxParamsForTicketableSchedulesRequest(),
    ...paramOverrides,
  }

  const endpoint = modeId === MODE_IDS.plane ? flightSchedulesEndpoint : ticketableSchedulesEndpoint

  dispatch(searchParamsOperations.saveSearchHistory())

  return sendApiRequest(state, endpoint, params)
}

// Sends a request to the endpoint. Adds the required API key and headers.
const sendApiRequest = (state, endpoint, extraParams) => {
  const params = {
    key: '86f5qBa1',
    ...extraParams,
  }

  return r2rServicesClient.get(endpoint, { params })
}

const responseHasErrorCode = (responseHeader, errorCode) => {
  if (responseHeader.errorDetails) {
    return contains(responseHeader.errorDetails, i => i.errorCode == errorCode)
  }
  return false
}

export default {
  performSearch,
  searchFailed,
  forceTimeChange,
  timeChange,
  ferryTypeChange,
  updateTransfer,
  fetchMore,
  updateUrl,
  refreshSearch,
  logReturnCheckoutCount,
  fetchDetails,
}
